import { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const ConfirmContext = createContext(null);

const DEFAULT = { open: false, title: 'Are you sure?', description: '', confirmLabel: 'Confirm', cancelLabel: 'Cancel', destructive: false };

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(DEFAULT);
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => new Promise((resolve) => {
    resolver.current = resolve;
    setState({
      open: true,
      title: opts.title || DEFAULT.title,
      description: opts.description || '',
      confirmLabel: opts.confirmLabel || DEFAULT.confirmLabel,
      cancelLabel: opts.cancelLabel || DEFAULT.cancelLabel,
      destructive: !!opts.destructive,
    });
  }), []);

  const settle = (val) => {
    setState((s) => ({ ...s, open: false }));
    if (resolver.current) { resolver.current(val); resolver.current = null; }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(o) => { if (!o) settle(false); }}>
        <AlertDialogContent className="bg-card border-border" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: '#010A21' }} data-testid="confirm-dialog-title">{state.title}</AlertDialogTitle>
            {state.description && <AlertDialogDescription data-testid="confirm-dialog-description">{state.description}</AlertDialogDescription>}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => settle(false)} data-testid="confirm-dialog-cancel">{state.cancelLabel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => settle(true)}
              className={state.destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : 'bg-primary text-primary-foreground'}
              data-testid="confirm-dialog-confirm"
            >{state.confirmLabel}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
}
